import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Transition } from 'react-transition-group'

import { useAppLayout } from '@/components/AppLayout/AppLayoutProvider'
import Button from '@/components/Button/Button'
import ToggleButton from '@/components/Button/ToggleButton'
import Eye, { EyeRef } from '@/components/Eye/Eye'
import UIIcon from '@/components/UI/UIIcon'
import { useAppState } from '@/composables/useAppState'
import useAudioMusic from '@/composables/useAudioMusic'
import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import useTracking from '@/composables/useTracking'
import { getAssetUrl } from '@/config/Assets'
import { CompletionStatus } from '@/state/AppStateMachine'
import { getContentfulUrl } from '@/utils/contentful'
import { safePlay } from '@/utils/media.utils'
import { getSubtitlePath } from '@/webgl/audio/AudioEngine'

import VideoAndSubtitles, { VideoAndSubtitlesRef } from '../Subtitles/VideoAndSubtitles'
import IntroEnterSwirl from './IntroEnterSwirl'

export type InterludeProps = React.HTMLProps<HTMLDivElement> & {
  name: string
  isVisible: boolean
}

export default function Interlude({ name, className, isVisible }: InterludeProps) {
  const { t } = useTranslation()
  const { actor, persona, scenario, scenarioUnitName } = useAppState()
  const hasSkippedScenarios = scenario.status.some((status) => status === CompletionStatus.SKIPPED)

  const { trackImpression, trackClick } = useTracking()
  const { gsap } = useGSAP()

  const music = useAudioMusic()
  useEffect(() => {
    // music.setVolume(0.4, 0.25);
    // return () => {
    //   music.setVolume(1.0, 0.5);
    // }
  }, [])

  // const onNext = useCallback(() => {
  //   actor.send({ type: 'NEXT' })
  //   trackClick({
  //     clickType: 'button',
  //     unitName: 'intro',
  //     unitLocation: null,
  //     buttonName: 'Next',
  //   })
  // }, [actor, trackClick]);

  const { footerRight } = useAppLayout()

  const elementRef = useRef<HTMLDivElement>(null)

  // const videoFilePath = `${persona}/audios/global_${name}.mp3`;
  // const videoSrc = getAudioPath(videoFilePath);
  const locale = 'en'
  const audioSubtitlesPath = `${persona}/audios/global_${name}.${locale}.vtt`
  const audioSubtitleSrc = getSubtitlePath(audioSubtitlesPath)

  const eyeRef = useRef<EyeRef>(null)

  const [entered, setEntered] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [exiting, setExiting] = useState(false)

  const needOpenEye = entered && loaded

  const [isPaused, setIsPaused] = useState(false)
  const [hasPlayOnce, setHasPlayOnce] = useState(false)
  const videoAndSubtitlesRef = useRef<VideoAndSubtitlesRef>(null)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (needOpenEye) {
        gsap
          .timeline()
          .addLabel('start', 0)
          // .add(() => eyeRef.current?.open({ toFull: true }), 'start')
          .add(() => eyeRef.current?.open({}), 'start')
          .add(() => eyeRef.current?.close({ blink: true }), 'start+=0.4')
          .add(() => eyeRef.current?.open({ delay: 0, toFull: true }), 'start+=1.2')
      }
    })

    return () => ctx.kill()
  }, [gsap, eyeRef, needOpenEye])

  const onExit = useCallback(() => {
    const ctx = gsap.context(() => {
      gsap
        .timeline({
          onComplete: () => {
            setEntered(false)
            setLoaded(false)
          },
        })
        .addLabel('start', 0)
        .add(() => eyeRef.current?.close({ blink: true, fromFull: true }), 'start')
    })

    return () => ctx.kill()
  }, [gsap, eyeRef, setEntered, setLoaded])

  const onEnter = useCallback(() => {
    trackImpression({
      unitName: `${persona}_simulation${scenarioUnitName}`,
      unitVariation: `${name}_meet_child`,
      unitLocation: 'tyce',
    })
    setEntered(true)
  }, [setEntered, trackImpression, name, persona, scenarioUnitName])

  const onLoaded = useCallback(() => {
    const paused = videoAndSubtitlesRef.current?.videoRef.current?.paused
    setIsPaused(paused)
    setLoaded(true)
  }, [setLoaded])

  const onSkip = useCallback(() => {
    setExiting(true)

    gsap.timeline().add(() => {
      actor.send({ type: 'SKIP' })
      trackClick({
        clickType: 'button',
        unitName: `${persona}_simulation${scenarioUnitName}`,
        unitVariation: `${name}_meet_child`,
        unitLocation: 'tyce',
        buttonName: 'navigation_button',
        buttonText: 'skip',
      })
    }, 0.7)
  }, [setExiting, gsap, actor, trackClick, name, persona, scenarioUnitName])

  const onNext = useCallback(() => {
    setExiting(true)

    gsap.timeline().add(() => {
      actor.send({ type: 'NEXT' })
    }, 0.7)
  }, [setExiting, gsap, actor])

  useLayoutEffect(() => {
    if (isVisible) {
      setExiting(false)
    }
  }, [isVisible, setExiting])

  const betterFormat = useImageFormatContext()
  const [imageUrl, setImageUrl] = useState(null)
  useEffect(() => {
    const url = getContentfulUrl(`/src/assets/images/interlude/${persona}.png`, { fm: betterFormat })
    setImageUrl(url)
  }, [persona, betterFormat])

  const onSkipAppeared = useCallback((button: HTMLElement) => {
    // button?.focus({ preventScroll: true });
  }, [])

  const hasTouch = 'ontouchstart' in window

  const showPlayButton = useMemo(() => {
    return loaded && isPaused && !hasPlayOnce && hasTouch && !exiting
  }, [hasTouch, loaded, isPaused, hasPlayOnce, exiting])

  const onPlay = useCallback(() => {
    if (!hasPlayOnce) {
      setHasPlayOnce(true)
    }
    setIsPaused(false)
  }, [setHasPlayOnce, setIsPaused, hasPlayOnce])

  const onPause = useCallback(() => {
    setIsPaused(true)
  }, [setIsPaused])

  const onToggleVideo = useCallback((paused: boolean) => {
    if (paused) {
      videoAndSubtitlesRef.current?.videoRef.current?.pause()
    } else {
      safePlay(videoAndSubtitlesRef.current?.videoRef.current)
    }
  }, [])

  return (
    <Transition
      nodeRef={elementRef}
      appear
      in={isVisible && !exiting}
      mountOnEnter
      unmountOnExit
      onEnter={onEnter}
      onExit={onExit}
      timeout={{
        enter: 1700,
        exit: 750,
      }}
      key={name}
    >
      <div
        data-testid='interlude'
        ref={elementRef}
        className={`Interlude ${className ? className : ''} relative w-full min-h-dvh md:h-screen`}
      >
        <IntroEnterSwirl className='absolute top-0 left-0 w-full h-full' />

        <Eye
          ref={eyeRef}
          className='relative w-full min-h-dvh h-dvh md:h-screen'
          defaultOpen={false}
          childrenBackground={<div className='absolute top-0 left-0 w-full h-full bg-dark-cream' />}
          isLargeMobile
        >
          {eyeRef && (
            <>
              <img
                className='absolute top-0 left-0 h-full w-full object-cover'
                src={imageUrl}
                alt={t(`child.${persona}.name`)}
              />
              <VideoAndSubtitles
                ref={videoAndSubtitlesRef}
                sources={{
                  mp4: getAssetUrl(`/src/assets/videos/interlude/${persona}_${name}.mp4`),
                  webm: getAssetUrl(`/src/assets/videos/interlude/${persona}_${name}.webm`),
                }}
                subtitlesSrc={audioSubtitleSrc}
                onEnded={onNext}
                onLoaded={onLoaded}
                onPlay={onPlay}
                onPause={onPause}
              />
            </>
          )}
        </Eye>
        {showPlayButton && (
          <div className='absolute z-80 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
            <Button appear appearDelay={0.5} className='PlayVideoButton'>
              {t('runner.play')}
            </Button>
          </div>
        )}

        {loaded &&
          footerRight.current &&
          createPortal(
            <div className='flex justify-end gap-12'>
              <ToggleButton checked={isPaused} setChecked={onToggleVideo} label={t('runner.pause')}>
                <div
                  className='absolute size-full inset-0 flex justify-center items-center'
                  title={isPaused ? t('runner.play') : t('runner.pause')}
                >
                  {!isPaused && (
                    <UIIcon
                      className='ToggleButton__Icon text-16 relative group-hover:scale-95 transition-transform duration-333 ease-out-quad text-dark-blue fill-current'
                      icon='pause'
                    />
                  )}
                  {isPaused && (
                    <UIIcon
                      className='ToggleButton__Icon text-16 relative group-hover:scale-95 transition-transform duration-333 ease-out-quad text-dark-blue fill-current'
                      icon='play'
                    />
                  )}
                </div>
              </ToggleButton>

              <Button appear onClick={onSkip} onAppeared={onSkipAppeared} className=''>
                {hasSkippedScenarios ? t('global.exit') : t('global.skip')}
              </Button>
            </div>,
            footerRight.current
          )}
      </div>
    </Transition>
  )
}
